import { useCallback, useEffect, useRef, useState } from 'react'
import { askForMore, credits, readMyAllowance, type MyAllowance } from '@shared/services/allowanceService'

/**
 * What is left of this month's allowance, beside WonderWorks in the bar at
 * the top of every page (Huanrui, 2026-10-19). The infinity sign while no
 * allowance is set. Used up: it says so and offers to ask the administrators
 * for more; once asked, it says that, until they answer.
 *
 * Read when the page opens, when the window comes back into view, every
 * minute, and when something that spends says so (allowanceMayHaveChanged).
 */
export function AllowanceBadge() {
  const [mine, setMine] = useState<MyAllowance | null>(null)
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const root = useRef<HTMLSpanElement | null>(null)

  const read = useCallback(() => {
    readMyAllowance()
      .then(setMine)
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    read()
    const every = window.setInterval(read, 60_000)
    const onShow = () => {
      if (document.visibilityState === 'visible') read()
    }
    document.addEventListener('visibilitychange', onShow)
    window.addEventListener('ww-allowance', read)
    return () => {
      window.clearInterval(every)
      document.removeEventListener('visibilitychange', onShow)
      window.removeEventListener('ww-allowance', read)
    }
  }, [read])

  // A click outside closes the box.
  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open])

  if (!mine) return null
  const unlimited = mine.left === null
  const out = !unlimited && (mine.left ?? 0) <= 0
  const low = !unlimited && !out && mine.limit !== null && (mine.left ?? 0) < mine.limit * 0.1

  const send = async () => {
    setSending(true)
    setError('')
    try {
      await askForMore(note)
      setMine({ ...mine, asked: true })
      setNote('')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'It could not be sent')
    } finally {
      setSending(false)
    }
  }

  return (
    <span className="ww-allow" ref={root}>
      <button
        type="button"
        className={`ww-allow__pill${out ? ' ww-allow__pill--out' : low ? ' ww-allow__pill--low' : ''}`}
        onClick={() => setOpen((was) => !was)}
        title={unlimited ? 'No monthly limit' : `${credits(mine.left ?? 0)} of ${credits(mine.limit ?? 0)} credits left this month`}
        aria-label={unlimited ? 'Credits left this month: no limit' : `Credits left this month: ${credits(mine.left ?? 0)}`}
      >
        {unlimited ? <span className="ww-allow__inf">∞</span> : out ? '0 left' : `${credits(mine.left ?? 0)} left`}
      </button>
      {open ? (
        <span className="ww-allow__box" role="dialog" aria-label="This month's allowance">
          <span className="ww-allow__title">This month</span>
          {unlimited ? (
            <span className="ww-allow__line">No limit. {credits(mine.used)} credits spent so far.</span>
          ) : (
            <>
              <span className="ww-allow__line">
                {credits(mine.used)} of {credits(mine.limit ?? 0)} credits spent · {credits(mine.left ?? 0)} left
              </span>
              <span className="ww-allow__bar">
                <span style={{ width: `${Math.min(100, (mine.used / Math.max(1, mine.limit ?? 1)) * 100)}%` }} />
              </span>
              <span className="ww-allow__quiet">1,000 credits = $1. A new allowance every month.</span>
            </>
          )}
          {!unlimited && mine.asked ? (
            <span className="ww-allow__line ww-allow__asked">Asked for more — an administrator will answer.</span>
          ) : !unlimited ? (
            <>
              <textarea
                className="ww-allow__note"
                rows={2}
                placeholder="What it is for (optional)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <button type="button" className="ww-allow__ask" disabled={sending} onClick={() => void send()}>
                {sending ? 'Sending…' : 'Ask for more'}
              </button>
            </>
          ) : null}
          {error ? <span className="ww-allow__error">{error}</span> : null}
        </span>
      ) : null}
    </span>
  )
}
